import { MediaUIAttributes } from 'media-chrome/dist/constants.js';
import { CaptionsRenderer } from 'media-captions';

/**
 * A custom element for displaying captions using Vidstack's captions renderer.
 *
 * @attr {string} mediasubtitlesshowing - (read-only) List of currently showing subtitles/captions
 * @attr {string} mediasubtitleslist - (read-only) List of all available subtitles/captions
 */
export class BunnyCaptionsDisplay extends HTMLElement {
  private mediaElement: HTMLMediaElement | null = null;
  private activeTrack: TextTrack | null = null;
  private renderer: CaptionsRenderer | null = null;
  private handleTimeUpdate: (() => void) | null = null;
  private handleTracksChange: ((e: Event) => void) | null = null;
  private handleIOSFullscreenBegin: (() => void) | null = null;
  private handleIOSFullscreenEnd: (() => void) | null = null;
  private trackModeBeforeFullscreen: TextTrackMode | null = null;
  private streamVideoElement: HTMLElement | null = null;
  private nativeCaptionStyle: HTMLStyleElement | null = null;
  private isInitialized: boolean = false;

  static get observedAttributes() {
    return [
      MediaUIAttributes.MEDIA_SUBTITLES_SHOWING,
      MediaUIAttributes.MEDIA_SUBTITLES_LIST,
    ];
  }

  constructor() {
    super();

    // Add positioning and variable mapping styles
    this.style.cssText = `
      position: absolute;
      inset: 0;
      pointer-events: none;
      display: flex;
      font-family: var(--captions-font-family);
      
      /* Map our custom variables to Vidstack's variables */
      --cue-color: var(--captions-text-color);
      --cue-bg-color: var(--captions-background);
      --cue-font-size: var(--captions-font-size);
      --cue-font-family: var(--captions-font-family);
      --cue-text-shadow: var(--captions-text-shadow);
      --cue-text-align: var(--captions-text-align);
      --cue-bottom: 10%;
      --cue-position-align: center;
      --cue-display-align: center;
      --cue-transform: translateY(calc(var(--captions-bottom-offset, 0) * -1));
    `;

    // Bind methods
    this.handleCueChange = this.handleCueChange.bind(this);
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;

    if (name === MediaUIAttributes.MEDIA_SUBTITLES_SHOWING) {
      this.updateActiveTrack();
    }
  }

  private initialize() {
    // Prevent duplicate initialization
    if (this.isInitialized) return;

    const streamVideoEl = this.parentElement;
    if (!streamVideoEl) return;

    this.streamVideoElement = streamVideoEl;

    // Find the video element (might not be ready immediately)
    const videoEl = this.findVideoElement(streamVideoEl);

    if (videoEl) {
      this.setup(videoEl);
    } else if (this.isConnected) {
      // Video element not ready yet, retry after a short delay
      requestAnimationFrame(() => {
        if (!this.isInitialized && this.isConnected) {
          this.initialize();
        }
      });
    }
  }

  private findVideoElement(
    streamVideoEl: HTMLElement,
  ): HTMLMediaElement | null {
    const videoEl = streamVideoEl.shadowRoot?.querySelector('video') ||
      streamVideoEl.querySelector('video');
    return videoEl instanceof HTMLMediaElement ? videoEl : null;
  }

  private setup(videoEl: HTMLMediaElement) {
    this.mediaElement = videoEl;

    // Create and inject the style that hides native captions
    this.createNativeCaptionHidingStyle();

    // Initialize renderer with this element as container
    if (!this.renderer) {
      this.renderer = new CaptionsRenderer(this);
    }

    this.setupTrackListeners();
    this.setupIOSFullscreenListeners();
    this.isInitialized = true;
  }

  private createNativeCaptionHidingStyle() {
    if (!this.streamVideoElement?.shadowRoot) return;

    // Check if style already exists (avoid duplicates)
    const existingStyle = this.streamVideoElement.shadowRoot.querySelector(
      '#caption-hide-style',
    );
    if (existingStyle instanceof HTMLStyleElement) {
      this.nativeCaptionStyle = existingStyle;
      return;
    }

    // Create the style element that hides native captions
    // This ensures we use custom captions instead of native ones
    this.nativeCaptionStyle = document.createElement('style');
    this.nativeCaptionStyle.id = 'caption-hide-style';
    this.nativeCaptionStyle.textContent = `
      video::cue {
        visibility: hidden !important;
        opacity: 0 !important;
        background: transparent !important;
        color: transparent !important;
        text-shadow: none !important;
      }
    `;
    this.streamVideoElement.shadowRoot.appendChild(this.nativeCaptionStyle);
  }

  private setupTrackListeners() {
    if (!this.mediaElement) return;

    // Avoid duplicate listeners
    if (this.handleTracksChange || this.handleTimeUpdate) {
      return;
    }

    // Listen for track changes
    this.handleTracksChange = this.updateActiveTrack.bind(this);
    this.mediaElement.textTracks.addEventListener(
      'change',
      this.handleTracksChange,
    );

    // Listen for time updates
    this.handleTimeUpdate = () => {
      if (this.renderer && this.mediaElement) {
        this.renderer.currentTime = this.mediaElement.currentTime;
      }
    };
    this.mediaElement.addEventListener('timeupdate', this.handleTimeUpdate);

    // Initial track setup
    this.updateActiveTrack();
  }

  private setupIOSFullscreenListeners() {
    if (!this.mediaElement) return;

    // Avoid duplicate listeners
    if (this.handleIOSFullscreenBegin || this.handleIOSFullscreenEnd) {
      return;
    }

    // iOS uses webkitbeginfullscreen/webkitendfullscreen events on the video element
    // These events are iOS-specific and won't fire on Chrome/Desktop browsers
    this.handleIOSFullscreenBegin = () => {
      // Remove the style that hides native captions so iOS can display them
      this.nativeCaptionStyle?.remove();

      // Enable the active track for iOS native caption display
      // iOS requires track.mode to be "showing" to display captions in fullscreen
      if (this.activeTrack) {
        this.trackModeBeforeFullscreen = this.activeTrack.mode;
        this.activeTrack.mode = 'showing';
      }

      // Hide custom captions when iOS enters native fullscreen
      this.style.display = 'none';
    };

    this.handleIOSFullscreenEnd = () => {
      // Restore the style that hides native captions
      if (
        this.nativeCaptionStyle &&
        !this.nativeCaptionStyle.parentNode &&
        this.streamVideoElement?.shadowRoot
      ) {
        this.streamVideoElement.shadowRoot.appendChild(this.nativeCaptionStyle);
      }

      // Restore the original track mode if we changed it
      if (this.activeTrack && this.trackModeBeforeFullscreen !== null) {
        this.activeTrack.mode = this.trackModeBeforeFullscreen;
        this.trackModeBeforeFullscreen = null;
      }

      // Show custom captions again when exiting iOS fullscreen
      this.style.display = 'flex';
    };

    // Listen for iOS-specific fullscreen events
    this.mediaElement.addEventListener(
      'webkitbeginfullscreen',
      this.handleIOSFullscreenBegin,
    );
    this.mediaElement.addEventListener(
      'webkitendfullscreen',
      this.handleIOSFullscreenEnd,
    );
  }

  private updateActiveTrack() {
    if (!this.mediaElement || !this.renderer) return;

    // Clean up previous track listener
    if (this.activeTrack) {
      this.activeTrack.removeEventListener('cuechange', this.handleCueChange);
    }

    // Get all tracks
    const tracks = Array.from(this.mediaElement.textTracks);

    // Find caption/subtitle tracks that are showing
    // Filter by kind first to exclude chapters and metadata tracks
    const showingTracks = tracks.filter((track) =>
      (track.kind === 'subtitles' || track.kind === 'captions') &&
      track.mode === 'showing'
    );

    // Use the first showing track
    this.activeTrack = showingTracks[0] || null;

    if (this.activeTrack) {
      this.activeTrack.addEventListener('cuechange', this.handleCueChange);
      this.handleCueChange(); // Initial render
    } else {
      this.renderer.reset();
    }
  }

  private handleCueChange() {
    if (!this.activeTrack || !this.renderer) return;

    // Reset cues
    this.renderer.reset();

    const cues = this.activeTrack.activeCues;
    if (!cues || cues.length === 0) {
      return;
    }

    // Add current cues
    Array.from(cues).forEach((cue) => {
      if (cue instanceof VTTCue) {
        // Set default position alignment if not set
        if (!cue.positionAlign) {
          cue.positionAlign = 'auto';
        }
        this.renderer?.addCue(cue);
      }
    });
  }

  private cleanup() {
    if (this.activeTrack) {
      this.activeTrack.removeEventListener('cuechange', this.handleCueChange);
    }

    if (this.mediaElement) {
      if (this.handleTimeUpdate) {
        this.mediaElement.removeEventListener(
          'timeupdate',
          this.handleTimeUpdate,
        );
      }
      if (this.handleTracksChange) {
        this.mediaElement.textTracks?.removeEventListener(
          'change',
          this.handleTracksChange,
        );
      }
      if (this.handleIOSFullscreenBegin) {
        this.mediaElement.removeEventListener(
          'webkitbeginfullscreen',
          this.handleIOSFullscreenBegin,
        );
      }
      if (this.handleIOSFullscreenEnd) {
        this.mediaElement.removeEventListener(
          'webkitendfullscreen',
          this.handleIOSFullscreenEnd,
        );
      }
    }

    if (this.renderer) {
      this.renderer.destroy();
      this.renderer = null;
    }

    this.mediaElement = null;
    this.activeTrack = null;
    this.handleTimeUpdate = null;
    this.handleTracksChange = null;
    this.handleIOSFullscreenBegin = null;
    this.handleIOSFullscreenEnd = null;
    this.trackModeBeforeFullscreen = null;
    this.streamVideoElement = null;
    this.nativeCaptionStyle = null;
    this.isInitialized = false;
  }
}

// Register the custom element
if (!customElements.get('bunny-captions-display')) {
  customElements.define('bunny-captions-display', BunnyCaptionsDisplay);
}

export default BunnyCaptionsDisplay;
